import React from 'react';
import { findKey, includes } from 'lodash';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';

import { Typography } from 'components/v2';
import { TBillingData } from './QuestionCopayExternalIdQuery';

interface Props extends WithStyles<typeof styles> {
  billingData: TBillingData;
  questionTextHTML: string;
}

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      paddingTop: '3rem',
    },
    commonText: {
      fontStyle: 'normal',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      marginBottom: `${unit}px`,
    },
    questionTitle: {
      display: 'inline-block',
      float: 'left',
      lineHeight: '24px',
      width: '100%',
    },
  });

const messages = defineMessages({
  statusNotPaid: {
    id: 'containers.PatientIntakeFormAnswersPage.QuestionCopay.statusNotPaid',
    defaultMessage: 'Not Paid',
  },
  statusPaid: {
    id: 'containers.PatientIntakeFormAnswersPage.QuestionCopay.statusPaid',
    defaultMessage: 'Paid',
  },
  statusPending: {
    id: 'containers.PatientIntakeFormAnswersPage.QuestionCopay.statusPending',
    defaultMessage: 'Pending',
  },
  statusSent: {
    id: 'containers.PatientIntakeFormAnswersPage.QuestionCopay.statusSent',
    defaultMessage: 'Sent',
  },
  statusUnknown: {
    id: 'containers.PatientIntakeFormAnswersPage.QuestionCopay.statusUnknown',
    defaultMessage: 'Unknown',
  },
});

const Question: React.FC<Props> = ({ classes, billingData, questionTextHTML }) => {
  const { formatMessage } = useIntl();
  const { name, transactionId, date, amount, vendor, vendorTransactionId, transactionLink, status } = billingData;

  const getStatusFormatted = (statusType: string) => {
    const transactionRawStatuses = {
      notPaid: [
        'cancel',
        'canceled',
        'declined',
        'error',
        'failed',
        'noPayment',
        'notPaid',
        'no-payment',
        'not-paid',
        'rejected',
      ],
      paid: ['paid', 'succeeded', 'success', 'successful'],
      pending: [
        'payment-processing',
        'processing',
        'requires_action',
        'requires_capture',
        'requires_confirmation',
        'requires_payment_method',
        'incomplete',
        'paymentProcessing',
        'pending',
        'waiting',
      ],
      sent: ['sent'],
      unknown: ['unknown'],
    };

    const transactionStatus = findKey(transactionRawStatuses, (rawStatuses) => includes(rawStatuses, statusType));

    const transactionStatuses: Record<string, string> = {
      notPaid: formatMessage(messages.statusNotPaid),
      paid: formatMessage(messages.statusPaid),
      pending: formatMessage(messages.statusPending),
      sent: formatMessage(messages.statusSent),
      unknown: formatMessage(messages.statusUnknown),
    };

    return transactionStatuses[transactionStatus || 'unknown'];
  };

  return (
    <>
      <Typography
        color="primary"
        variant="bW600F16"
        className={classes.questionTitle}
        gutterBottom
        component="span"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: questionTextHTML }}
      />

      <div className={classes.root}>
        <Typography className={classes.commonText}>
          <strong>Billing Item: </strong>
          {name}
        </Typography>
        <Typography className={classes.commonText}>
          <strong>Status: </strong>
          {getStatusFormatted(status)}
        </Typography>
        {status === 'successful' && (
          <>
            <Typography className={classes.commonText}>
              <strong>Luma Transaction Id: </strong>
              {transactionId}
            </Typography>
            <Typography className={classes.commonText}>
              <strong>Date: </strong>
              {date}
            </Typography>
          </>
        )}
        <Typography className={classes.commonText}>
          <strong>Amount: </strong>
          {amount}
        </Typography>
        {status === 'successful' && (
          <>
            <Typography className={classes.commonText}>
              <strong>Vendor: </strong>
              {vendor?.toUpperCase()}
            </Typography>
            <Typography className={classes.commonText}>
              <strong>Vendor Transaction Id: </strong>
              {vendorTransactionId}
            </Typography>
            <Typography className={classes.commonText}>
              <strong>Transaction Link: </strong>
              {transactionLink}
            </Typography>
          </>
        )}
      </div>
    </>
  );
};

export default withStyles(styles)(Question);
